import { memoizeFunction } from '@agilepoint/utils-common';
import { ICalloutPositionStyles, ICalloutContentProps } from './callout-types';
import { defaultProps, getDefaultPositionStyles } from './callout-default-props';

const MAX_HEIGHT_FACTOR = 0.95;
const MAX_WIDTH_FACTOR = 0.95;
const OFFSET_ADJUSTMENT = 0;

interface IComputeCalloutPositionArgs extends Pick<ICalloutContentProps, 'getSurfaceClientRect'> {
    targetElement: HTMLElement;
    offset: number;
    beakWidth: number;
    isBeakVisible?: boolean;
    useTargetsWidth?: boolean;
    useFullHeight?: boolean;
    useFullWidth?: boolean;
    renderAdjacentToTarget?: boolean;
}

const computeCalloutPosition = memoizeFunction((args: IComputeCalloutPositionArgs): ICalloutPositionStyles => {
    const {
        targetElement,
        offset,
        beakWidth,
        isBeakVisible,
        useTargetsWidth,
        useFullHeight,
        useFullWidth,
        renderAdjacentToTarget,
        getSurfaceClientRect
    } = args;
    const contentPositionStyles: ICalloutPositionStyles['contentPositionStyles'] = {};
    const beakStyles: ICalloutPositionStyles['beakStyles'] = {};
    const targetClientRect = targetElement.getBoundingClientRect();
    const { height, width } = targetClientRect;
    const surfaceClientRect = getSurfaceClientRect();
    const surfaceHeight = surfaceClientRect.height;
    const surfaceWidth = surfaceClientRect.width;
    const top = targetClientRect.top - surfaceClientRect.top;
    const left = targetClientRect.left - surfaceClientRect.left;
    const bottom = surfaceClientRect.bottom - targetClientRect.bottom;
    const availableSpaceBelow = surfaceHeight - (top + height);
    const availableSpaceTop = top;
    const availableSpaceLeft = left;
    const availableSpaceRight = surfaceWidth - (left + width);
    const beakVisibilitySize = beakWidth * 0.7071067811865476;
    const beakVerticalPosition = renderAdjacentToTarget ? ((height - beakWidth) * 0.5) : ((beakWidth * 0.20710678118654757) - beakVisibilitySize);
    const beakHorizontalPosition = renderAdjacentToTarget ? ((beakWidth * 0.20710678118654757) - beakVisibilitySize) : (width * 0.2);
    const computedOffsetWithBreak = offset + (isBeakVisible ? beakVisibilitySize : 0);
    const maxHeightFactor = useFullHeight ? 1 : MAX_HEIGHT_FACTOR;
    const maxWidthFactor = useFullWidth ? 1 : MAX_WIDTH_FACTOR;

    // Set vertical position
    if (availableSpaceBelow >= availableSpaceTop) {
        const computedTopPosition = renderAdjacentToTarget ? top : (height + top + computedOffsetWithBreak);
        contentPositionStyles.top = computedTopPosition;
        contentPositionStyles.maxHeight = (surfaceHeight - computedTopPosition) * maxHeightFactor;
        beakStyles.top = beakVerticalPosition;
    } else {
        const computedBottomPosition = renderAdjacentToTarget ? bottom : (surfaceHeight - top + computedOffsetWithBreak);
        contentPositionStyles.bottom = computedBottomPosition;
        contentPositionStyles.maxHeight = (surfaceHeight - computedBottomPosition) * maxHeightFactor;
        beakStyles.bottom = beakVerticalPosition;
    }

    // Set horizontal position
    if (availableSpaceRight >= availableSpaceLeft) {
        const computedLeftPosition = renderAdjacentToTarget ? (left + width + computedOffsetWithBreak) : left;
        contentPositionStyles.left = computedLeftPosition;
        contentPositionStyles.maxWidth = (surfaceWidth - computedLeftPosition) * maxWidthFactor;
        beakStyles.left = beakHorizontalPosition;
    } else {
        const computedRightPosition = renderAdjacentToTarget ? (surfaceWidth - left + computedOffsetWithBreak) : (surfaceWidth - (left + width));
        contentPositionStyles.right = computedRightPosition;
        contentPositionStyles.maxWidth = (surfaceWidth - computedRightPosition) * maxWidthFactor;
        beakStyles.right = beakHorizontalPosition;
    }

    contentPositionStyles.width = useTargetsWidth === true ? width : 'unset';

    return { contentPositionStyles, beakStyles };
});

interface IPointerPosition {
    clientX: number;
    clientY: number;
}

interface IComputeCalloutPointerPositionArgs extends Omit<IComputeCalloutPositionArgs, 'targetElement' | 'renderAdjacentToTarget'>, IPointerPosition {
}

function computeCalloutPositionForPointer(args: IComputeCalloutPointerPositionArgs): ICalloutPositionStyles {
    const contentPositionStyles: ICalloutPositionStyles['contentPositionStyles'] = {};
    const { clientX, clientY, getSurfaceClientRect } = args;
    const surfaceClientRect = getSurfaceClientRect();
    const surfaceHeight = surfaceClientRect.height;
    const surfaceWidth = surfaceClientRect.width;
    const availableSpaceRight = surfaceWidth - clientX;
    const availableSpaceLeft = clientX;
    const availableSpaceTop = clientY;
    const availableSpaceBelow = surfaceHeight - clientY;
    const maxHeightFactor = args.useFullHeight ? 1 : MAX_HEIGHT_FACTOR;
    const maxWidthFactor = args.useFullWidth ? 1 : MAX_WIDTH_FACTOR;

    // Set horizontal position
    if (availableSpaceRight >= availableSpaceLeft) {
        contentPositionStyles.left = clientX - surfaceClientRect.left;
        contentPositionStyles.right = '';
        contentPositionStyles.maxWidth = (surfaceWidth - contentPositionStyles.left) * maxWidthFactor;
    } else {
        contentPositionStyles.right = availableSpaceRight + surfaceClientRect.left;
        contentPositionStyles.left = '';
        contentPositionStyles.maxWidth = (surfaceWidth - contentPositionStyles.right) * maxWidthFactor;
    }

    // Set vertical position
    if (availableSpaceBelow >= availableSpaceTop) {
        contentPositionStyles.top = clientY - surfaceClientRect.top;
        contentPositionStyles.maxHeight = (surfaceHeight - contentPositionStyles.top) * maxHeightFactor;

    } else {
        contentPositionStyles.bottom = availableSpaceBelow + surfaceClientRect.top;
        contentPositionStyles.maxHeight = (surfaceHeight - contentPositionStyles.bottom) * maxHeightFactor;
    }
    return { contentPositionStyles };
}

/**
 * Version argument is required in below method to recompute position on window resize.
 */
export const getCalloutPosition = memoizeFunction((version: number, props: ICalloutContentProps): ICalloutPositionStyles => {
    const { targetRef,
        beakWidth = defaultProps.beakWidth,
        isBeakVisible,
        offset = defaultProps.offset,
        useTargetsWidth,
        useFullHeight,
        useFullWidth,
        pointerPosition,
        renderAdjacentToTarget,
        getSurfaceClientRect
    } = props;

    const adjustedOffset = offset + OFFSET_ADJUSTMENT;

    if (pointerPosition) {
        return computeCalloutPositionForPointer({
            offset: adjustedOffset,
            beakWidth,
            isBeakVisible,
            useTargetsWidth,
            useFullHeight,
            useFullWidth,
            clientX: pointerPosition.clientX,
            clientY: pointerPosition.clientY,
            getSurfaceClientRect
        });
    }

    if (targetRef === undefined || targetRef.current === null) {
        return getDefaultPositionStyles(useFullHeight, useFullWidth);
    }

    return computeCalloutPosition({
        targetElement: targetRef.current,
        offset: adjustedOffset,
        beakWidth,
        isBeakVisible,
        useTargetsWidth,
        useFullHeight,
        useFullWidth,
        renderAdjacentToTarget,
        getSurfaceClientRect
    });
});
